import { useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from '~/components/ui/Button';
import { IconButton } from '~/components/ui/IconButton';
import { deleteOrganization } from '~/lib/.client/api/organizations';
import { WINDOW_RELOAD_TIMEOUT } from '~/lib/constants';
import { teamStore } from '~/lib/stores/team';
import type { UserData } from '~/types/user';
import { classNames } from '~/utils/classNames';
import { withSpinner } from '~/utils/withSpinner';

export function FreeTeamBanner({ user }: { user: UserData }) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [visible, setVisible] = useState(true);

  const [dismissed, setDismissed] = useState(() => !!globalThis.localStorage?.getItem('free-team-banner-dismissed'));

  const freeTeam = user.organizations?.find((org) => !org.billing);
  const isAdmin = freeTeam?.role === 'admin';

  if (!user?.featureFlags.teamManagement || dismissed || !visible || !freeTeam || !isAdmin) {
    return null;
  }

  const dismiss = () => {
    localStorage.setItem('free-team-banner-dismissed', new Date().toISOString());
    setDismissed(true);
    setVisible(false);
  };

  const handleDeleteOrg = () => {
    withSpinner({
      action: async () => {
        try {
          await deleteOrganization({ org: freeTeam });
          dismiss();
          toast.success('Your team has been discarded.');

          setTimeout(() => {
            window.location.reload();
          }, WINDOW_RELOAD_TIMEOUT);
        } catch (error) {
          setVisible(false);
          toast.error(error instanceof Error ? error.message : 'An unknown error occurred');
        }
      },
      onStateUpdate: setIsDeleting,
    });
  };

  return (
    <div
      className={classNames(
        'border-bolt-ds-brand-container bg-bolt-ds-brandContainer',
        'fixed z-max bottom-8 right-8',
        'flex items-start gap-3',
        'w-111 max-w-full',
        'pl-4 pr-8 py-4',
        'border border-bolt-ds-borderOutline rounded-md',
        'select-none',
        'backdrop-blur-2xl shadow-sm',
      )}
    >
      <span className="i-ph:info text-xl text-bolt-ds-brand" />

      <div className="flex flex-col gap-1 max-w-90">
        <span className="text-bolt-ds-brand font-semibold text-sm">Resume {freeTeam.name} Team setup?</span>

        <div className="text-sm text-bolt-elements-textSecondary">
          You started creating a team but haven’t finished yet. Choose a plan now or discard it and start fresh later.
        </div>

        <div className="flex gap-3">
          <Button
            variant="link-color"
            color="brand"
            onClick={() => {
              teamStore.createTeamData.set({
                organization: freeTeam,
                level: freeTeam.setupState?.level ?? 'teams_1',
                interval: freeTeam.setupState?.interval ?? 'monthly',
                members: [],
              });
              setVisible(false);
            }}
          >
            Resume Team Setup
          </Button>
          <Button variant="link-gray" onClick={handleDeleteOrg} disabled={isDeleting} loading={isDeleting}>
            Discard
          </Button>
        </div>
      </div>

      <IconButton icon="i-ph:x" size="2xs" variant="ghost" className="absolute top-2 right-2" onClick={dismiss} />
    </div>
  );
}
